import { useState, useRef, useCallback } from 'react';
import { useUploadDocumentMutation } from '../../../../../../services/RefundService';
import { RefundView } from '../../../../../../services/RefundService/types/refundTypes';
import { isSupportedFileType, isValidFileSize, ALLOWED_EXTENSIONS } from '../constants';

export interface FileWithPreview {
  file: File;
  id: string;
}

export interface UseDocumentUploadReturn {
  selectedFiles: FileWithPreview[];
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  isDragOver: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDragOver: (event: React.DragEvent) => void;
  handleDragLeave: (event: React.DragEvent) => void;
  handleDrop: (event: React.DragEvent) => void;
  handleRemoveFile: (fileId: string) => void;
  handleUpload: () => Promise<void>;
  handleClose: () => void;
  handleBrowseFiles: () => void;
}

export interface UseDocumentUploadOptions {
  refund: RefundView;
  onClose: () => void;
}

export function useDocumentUpload({
  refund,
  onClose,
}: UseDocumentUploadOptions): UseDocumentUploadReturn {
  const [uploadDocument] = useUploadDocumentMutation();
  const [selectedFiles, setSelectedFiles] = useState<FileWithPreview[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback((files: File[]) => {
    const validFiles: FileWithPreview[] = [];
    let hasErrors = false;

    files.forEach((file) => {
      if (!isValidFileSize(file.size)) {
        setError(`File "${file.name}" exceeds the 10MB size limit.`);
        hasErrors = true;
        return;
      }

      if (!isSupportedFileType(file.name)) {
        setError(
          `File "${file.name}" has an unsupported format. Allowed formats: ${ALLOWED_EXTENSIONS.join(', ')}`
        );
        hasErrors = true;
        return;
      }

      validFiles.push({ file, id: `${Date.now()}-${Math.random()}` });
    });

    if (!hasErrors) {
      setError(null);
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    }
  }, []);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files || []);
      processFiles(files);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [processFiles]
  );

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      const files = Array.from(event.dataTransfer.files || []);
      processFiles(files);
    },
    [processFiles]
  );

  const handleRemoveFile = useCallback((fileId: string) => {
    setSelectedFiles((prev) => prev.filter((f) => f.id !== fileId));
  }, []);

  const handleUpload = useCallback(async () => {
    if (!refund || selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      const totalFiles = selectedFiles.length;
      for (let i = 0; i < selectedFiles.length; i++) {
        const { file } = selectedFiles[i];
        await uploadDocument({
          company: refund.company,
          center: refund.center,
          refundId: refund.id,
          file,
          documentType: 'General',
        }).unwrap();
        setUploadProgress(((i + 1) / totalFiles) * 100);
      }
      resetState();
      onClose();
    } catch (err: any) {
      setError(err?.data?.message || 'Failed to upload documents. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [refund, selectedFiles, uploadDocument, onClose]);

  const resetState = useCallback(() => {
    setSelectedFiles([]);
    setError(null);
    setUploadProgress(0);
    setIsDragOver(false);
  }, []);

  const handleClose = useCallback(() => {
    if (!isUploading) {
      resetState();
      onClose();
    }
  }, [isUploading, resetState, onClose]);

  const handleBrowseFiles = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    selectedFiles,
    isUploading,
    uploadProgress,
    error,
    isDragOver,
    fileInputRef,
    handleFileSelect,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleRemoveFile,
    handleUpload,
    handleClose,
    handleBrowseFiles,
  };
}
